import { Injectable, NgZone, OnDestroy, PLATFORM_ID, WritableSignal, inject, signal } from '@angular/core';
import { Location, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { Observable, finalize, map } from 'rxjs';
import { AccountService } from '../account.service';
import { NotificationService } from '../notification/notification.service';
import { LocalStorageService } from '../local-storage.service';
import { LOCALSTORAGE_KEYS } from '../constants/databases';
import { HTTP_STATUS_CODES } from '../constants/errors';

/** Carries the token from the emailed link back into the signup route. */
export const VERIFICATION_TOKEN_QUERY_PARAM = 'verificationToken';

const VERIFICATION_CHANNEL = 'signup-email-verification';
const RESEND_COOLDOWN_SECONDS = 60;

export type EmailVerificationError = 'expired' | 'invalid';

/**
 * What the backend keeps while the customer goes to their inbox. `state` is whatever the brand
 * needs to resume its own step 1, so it stays untyped here.
 */
export interface EmailVerificationRecord<TState = any> {
  email: string;
  state: TState;
}

/**
 * The email-verification step of the signup, minus anything brand-specific (DEV-11124 / ELIO-6959).
 *
 * It owns the mechanics that are identical everywhere — sending and resending with a cooldown,
 * consuming the token from the link, and keeping every open tab in sync — so that porting the
 * step to a brand is wiring, not a copy of 150 lines into a `signup.component.ts` that is already
 * different in each one.
 *
 * Two things deliberately stay with the caller: building the state to store (each brand resumes
 * different fields) and applying the restored record (each brand assigns it to its own stepper).
 *
 * Provide it on the signup component, not in root: its state belongs to one signup attempt.
 */
@Injectable()
export class EmailVerificationService implements OnDestroy {

  #platformId = inject(PLATFORM_ID);
  #accountService = inject(AccountService);
  #notificationService = inject(NotificationService);
  #localStorageService = inject(LocalStorageService);
  #router = inject(Router);
  #location = inject(Location);
  #ngZone = inject(NgZone);

  /** This tab is showing the blocking "check your email" screen. */
  pending = signal(false);

  /** The link was opened somewhere else, so this tab has nothing left to do. */
  verifiedInOtherTab = signal(false);

  /** This tab is the one consuming the token from the link. */
  isVerifyingToken = signal(false);

  isSending = signal(false);

  resendCooldown = signal(0);

  error: WritableSignal<EmailVerificationError | null> = signal(null);

  /** Token consumed from the link. The account-creation endpoint rejects a signup without it. */
  token: string | null = null;

  /** Address the token proved, so a resumed signup can tell it apart from an edited one. */
  verifiedEmail: string | null = null;

  #channel: BroadcastChannel | null = null;
  #storageListener: ((e: StorageEvent) => void) | null = null;
  #cooldownIntervalId: any = null;
  #onSessionCreatedElsewhere: (() => void) | null = null;

  ngOnDestroy(): void {
    clearInterval(this.#cooldownIntervalId);
    this.#channel?.close();
    if (this.#storageListener)
      window.removeEventListener('storage', this.#storageListener);
  }

  /**
   * Stores the pending signup and emails the link. `state` is handed back untouched when the
   * link is opened, so put in it everything the brand needs to resume step 1.
   */
  send<TState>(email: string, state: TState, isFirstSend = false): void {
    if (this.isSending() || (!isFirstSend && this.resendCooldown() > 0) || !email) return;

    this.isSending.set(true);

    this.#accountService
      .createSignupVerification(email, state as any)
      .pipe(finalize(() => this.isSending.set(false)))
      .subscribe({
        next: (res: any) => {
          if (res?.error && res.status !== HTTP_STATUS_CODES.TOO_MANY_REQUESTS) {
            this.#notificationService.show({
              text: res.message || 'We could not send the verification email. Please try again.',
              type: 'error'
            });
            return;
          }

          if (res?.error)
            this.#notificationService.show({
              text: 'A verification email was sent recently. Please check your inbox.',
              type: 'warning'
            });
          else if (!isFirstSend)
            this.#notificationService.show({
              text: `Verification email sent to ${email}.`,
              type: 'success'
            });

          this.error.set(null);
          this.pending.set(true);
          this.#startResendCooldown();
          this.initSync();
        }
      });
  }

  /**
   * Trades the token from the link for the stored record. Emits null — and leaves `error` set —
   * when the link is spent or bogus, so the caller only has to handle the happy path.
   */
  consume(token: string): Observable<EmailVerificationRecord | null> {
    this.isVerifyingToken.set(true);

    return this.#accountService
      .getSignupVerification(token)
      .pipe(
        finalize(() => this.isVerifyingToken.set(false)),
        map((res: any) => {
          // The backend returns the record either raw ({ email, state }) or inside the
          // standard ApiResponse envelope ({ data: { email, state } }).
          const record = (res?.data ?? res) as EmailVerificationRecord;
          if (res?.error || !record?.email) {
            this.error.set(res?.status === HTTP_STATUS_CODES.GONE ? 'expired' : 'invalid');
            this.stripTokenFromUrl();
            return null;
          }
          return record;
        })
      );
  }

  /** Records the proven address and tells the other tabs to stand down. */
  markVerified(token: string, email: string): void {
    this.token = token;
    this.verifiedEmail = email;
    this.pending.set(false);
    this.#broadcastVerified(email);
  }

  /** True when this tab already holds a token for that exact address. */
  isVerified(email: string): boolean {
    return !!this.token && this.verifiedEmail === email;
  }

  /** Back to step 1 so the customer can fix a typo in the address. */
  changeEmail(): void {
    this.pending.set(false);
  }

  restart(): void {
    this.error.set(null);
  }

  /**
   * The token stays in the URL on purpose: the GET is idempotent within its TTL, so a refresh
   * re-consumes it and restores the signup instead of starting over. This is for the paths that
   * do need it gone — a spent or invalid link.
   */
  stripTokenFromUrl(): void {
    const urlTree = this.#router.parseUrl(this.#router.url);
    delete urlTree.queryParams[VERIFICATION_TOKEN_QUERY_PARAM];
    this.#location.replaceState(urlTree.toString());
  }

  /**
   * Keeps the tab that started the signup in step with the one opened from the link.
   * `onSessionCreatedElsewhere` fires when the account is finished in that other tab.
   */
  initSync(onSessionCreatedElsewhere?: () => void): void {
    if (onSessionCreatedElsewhere) this.#onSessionCreatedElsewhere = onSessionCreatedElsewhere;
    if (!isPlatformBrowser(this.#platformId) || this.#storageListener) return;

    if ('BroadcastChannel' in window) {
      this.#channel = new BroadcastChannel(VERIFICATION_CHANNEL);
      this.#channel.onmessage = (e: MessageEvent) =>
        this.#ngZone.run(() => this.#onVerifiedSomewhereElse(e.data));
    }

    // Fallback for browsers without BroadcastChannel, plus session detection so this tab
    // leaves once the account is fully created in the other one.
    this.#storageListener = (e: StorageEvent) => this.#ngZone.run(() => {
      if (e.key === LOCALSTORAGE_KEYS.SIGNUP_EMAIL_VERIFIED && e.newValue)
        this.#onVerifiedSomewhereElse({ type: 'verified' });

      if (e.key === LOCALSTORAGE_KEYS.SESSION && e.newValue && (this.verifiedInOtherTab() || this.pending()))
        this.#onSessionCreatedElsewhere?.();
    });
    window.addEventListener('storage', this.#storageListener);
  }

  #onVerifiedSomewhereElse(data: any): void {
    if (data?.type !== 'verified' || !this.pending()) return;
    this.pending.set(false);
    this.verifiedInOtherTab.set(true);
    clearInterval(this.#cooldownIntervalId);
  }

  #broadcastVerified(email: string): void {
    if (!isPlatformBrowser(this.#platformId)) return;

    try {
      if ('BroadcastChannel' in window) {
        const channel = new BroadcastChannel(VERIFICATION_CHANNEL);
        channel.postMessage({ type: 'verified', email });
        channel.close();
      }
    } catch { /* sync is best-effort; verification itself already succeeded */ }

    // storage-event fallback: fires only in the other tabs.
    this.#localStorageService.set(LOCALSTORAGE_KEYS.SIGNUP_EMAIL_VERIFIED, { email, verifiedAt: new Date().toISOString() });
    this.#localStorageService.remove(LOCALSTORAGE_KEYS.SIGNUP_EMAIL_VERIFIED);
  }

  #startResendCooldown(): void {
    if (!isPlatformBrowser(this.#platformId)) return;
    clearInterval(this.#cooldownIntervalId);
    this.resendCooldown.set(RESEND_COOLDOWN_SECONDS);
    this.#cooldownIntervalId = setInterval(() => {
      const remaining = this.resendCooldown() - 1;
      this.resendCooldown.set(Math.max(0, remaining));
      if (remaining <= 0) clearInterval(this.#cooldownIntervalId);
    }, 1000);
  }
}
